"use client";

import { useState, useRef, useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

interface FormData {
  name: string;
  email: string;
  date: string;
  service: string;
}

const services = [
  "General Gynecological Care",
  "Fertility Care",
  "PCOS Clinic",
  "Endometriosis Clinic",
  "Obstetric Care",
  "Family Planning",
  "ANC Services",
  "Laboratory Services",
  "Ultrasonography"
];

export default function BookingForm() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormData>({
    name: "",
    email: "",
    date: "",
    service: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/book-appointment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit appointment");
      }

      setSubmitted(true);
      setForm({ name: "", email: "", date: "", service: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      ref={sectionRef}
      className="relative py-16 sm:py-20 md:py-24 px-4 sm:px-6 overflow-hidden"
    >
      {/* Background with gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-royal-purple/5 via-pink-pale to-diamond" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-10 w-96 h-96 bg-light-purple rounded-full mix-blend-multiply filter blur-3xl animate-float-b" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-pink-brand rounded-full mix-blend-multiply filter blur-3xl animate-float-c" />
      </div>

      <div className="relative max-w-4xl mx-auto">
        {/* Section Header */}
        <div
          className={`text-center mb-8 sm:mb-10 md:mb-12 transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-pink-brand/20 border border-pink-brand/40 text-pink-brand text-xs font-bold uppercase tracking-wider mb-3 sm:mb-4">
            <Icon name="CalendarDaysIcon" size={14} variant="solid" />
            Book Your Appointment
          </div>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-royal-purple mb-3 sm:mb-4 px-4">
            Schedule Your <span className="gradient-text-gold">Consultation</span>
          </h2>
          <p className="text-brand-muted text-base sm:text-lg max-w-2xl mx-auto px-4">
            Fill out the form below and we'll get back to you shortly to confirm your appointment
          </p>
        </div>

        {/* Form Card */}
        <div
          className={`bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl border border-pink-brand/20 transition-all duration-1000 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          {submitted ? (
            <div className="text-center py-8 sm:py-12 space-y-3 sm:space-y-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-pink-brand/20 flex items-center justify-center mx-auto animate-scale-in">
                <Icon name="CheckCircleIcon" size={36} variant="solid" className="text-pink-brand sm:w-10 sm:h-10" />
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-royal-purple px-4">
                Appointment Request Received!
              </h3>
              <p className="text-brand-muted text-sm sm:text-base max-w-md mx-auto px-4">
                Thank you for booking with The Gyn Consult. We've received your appointment request and will contact you shortly to confirm.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 mt-3 sm:mt-4 text-sm font-semibold text-pink-brand hover:text-royal-purple transition-colors"
              >
                Book Another Appointment
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
              {/* Name Field */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-royal-purple mb-2"
                >
                  Full Name <span className="text-pink-brand">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border-2 border-silver-light bg-white text-royal-purple placeholder:text-brand-muted focus:border-pink-brand focus:ring-2 focus:ring-pink-brand/20 outline-none transition-all duration-300"
                />
              </div>

              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-royal-purple mb-2"
                >
                  Email Address <span className="text-pink-brand">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border-2 border-silver-light bg-white text-royal-purple placeholder:text-brand-muted focus:border-pink-brand focus:ring-2 focus:ring-pink-brand/20 outline-none transition-all duration-300"
                />
              </div>

              {/* Date Field */}
              <div>
                <label
                  htmlFor="date"
                  className="block text-sm font-semibold text-royal-purple mb-2"
                >
                  Preferred Date <span className="text-pink-brand">*</span>
                </label>
                <input
                  id="date"
                  name="date"
                  type="date"
                  required
                  value={form.date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border-2 border-silver-light bg-white text-royal-purple focus:border-pink-brand focus:ring-2 focus:ring-pink-brand/20 outline-none transition-all duration-300"
                />
              </div>

              {/* Service Dropdown */}
              <div>
                <label
                  htmlFor="service"
                  className="block text-sm font-semibold text-royal-purple mb-2"
                >
                  Service Required <span className="text-pink-brand">*</span>
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={form.service}
                  onChange={handleChange}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-xl border-2 border-silver-light bg-white text-royal-purple focus:border-pink-brand focus:ring-2 focus:ring-pink-brand/20 outline-none transition-all duration-300"
                >
                  <option value="">Select a service...</option>
                  {services.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 sm:p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs sm:text-sm">
                  <Icon name="ExclamationCircleIcon" size={20} variant="solid" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-brand to-[#FF1493] text-white font-bold py-3 sm:py-4 px-6 sm:px-8 text-sm sm:text-base rounded-xl shadow-gold hover:shadow-gold-lg transition-all duration-500 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3 group"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Icon
                      name="CalendarDaysIcon"
                      size={20}
                      variant="solid"
                      className="transition-transform duration-300 group-hover:scale-110"
                    />
                    <span>Book Appointment</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Contact Info */}
        <div
          className={`text-center mt-8 transition-all duration-1000 delay-400 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <p className="text-brand-muted text-sm">
            Need immediate assistance?{" "}
            <a
              href="https://wa.me/256775211731"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-brand font-semibold hover:text-royal-purple transition-colors"
            >
              WhatsApp us at +256 775 211 731
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}